import { useState } from 'react';
import { createPortal } from 'react-dom';
import { C, t } from '../constants/dashboardConstants';
import { DEFAULT_SCHEDULE_COLOR, SCHEDULE_COLOR_OPTIONS, normalizeScheduleColor } from './scheduleColors';

const labelStyle = t('20px', 500, C.bk);

const inputStyle = {
  width: '100%', height: '60px', padding: '0 18px',
  background: '#FCFCFD', border: `1px solid #E9E9EC`,
  borderRadius: '10px', boxSizing: 'border-box',
  fontFamily: 'Pretendard, sans-serif', fontSize: '18px',
  fontWeight: 400, letterSpacing: '-0.04em', outline: 'none',
  color: '#090909',
};

function parseIso(isoStr) {
  if (!isoStr) return { date: '', time: '' };
  const d = new Date(isoStr);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return { date, time };
}

function toIso(date, time) {
  if (!date) return '';
  return `${date}T${time || '00:00'}:00`;
}

export default function AddEventModal({ initialData, initialDate, onClose, onSave }) {
  const isEdit = !!initialData;

  const startParsed = parseIso(initialData?.startAt);
  const endParsed = parseIso(initialData?.endAt);

  const [title, setTitle] = useState(initialData?.title || '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [startDate, setStartDate] = useState(startParsed.date || initialDate || '');
  const [startTime, setStartTime] = useState(startParsed.time || '');
  const [endDate, setEndDate] = useState(endParsed.date || initialDate || '');
  const [endTime, setEndTime] = useState(endParsed.time || '');
  const [location, setLocation] = useState(initialData?.location || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [color, setColor] = useState(normalizeScheduleColor(initialData?.color || initialData?.eventColor || DEFAULT_SCHEDULE_COLOR));

  const handleSave = () => {
    if (!title.trim() || !startDate) return;
    onSave({
      title,
      category,
      description,
      startAt: toIso(startDate, startTime),
      endAt: toIso(endDate || startDate, endTime || startTime),
      location,
      color,
    });
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#FFFFFF', borderRadius: '20px',
          padding: '60px 50px', width: '780px', maxHeight: '90vh',
          overflowY: 'auto', boxSizing: 'border-box',
          scrollbarWidth: 'thin', scrollbarColor: '#E9E9EC transparent',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
            <span style={t('36px', 600, '#090909')}>{isEdit ? '일정 수정' : '새 일정 추가'}</span>
            <span style={t('16px', 400, '#A2A2A2')}>{isEdit ? '일정을 수정하세요.' : '새로운 일정을 추가하세요.'}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#888896" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
          {/* 제목 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={labelStyle}>제목</span>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="일정 제목" style={inputStyle} />
          </div>

          {/* 카테고리 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={labelStyle}>카테고리</span>
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="예: 공고, 미팅, 촬영, 리허설..." style={inputStyle} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={labelStyle}>색상</span>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {SCHEDULE_COLOR_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColor(option)}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: option,
                    cursor: 'pointer',
                    border: color === option ? '2px solid #090909' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-label={`색상 ${option}`}
                >
                  {color === option && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 시작 날짜 + 시간 */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span style={labelStyle}>시작 날짜</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span style={labelStyle}>시작 시간</span>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </div>
          </div>

          {/* 종료 날짜 + 시간 */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span style={labelStyle}>종료 날짜</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span style={labelStyle}>종료 시간</span>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </div>
          </div>

          {/* 장소 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={labelStyle}>장소</span>
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder="장소 (선택)" style={inputStyle} />
          </div>

          {/* 세부 정보 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={labelStyle}>세부 정보</span>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="세부 내용을 입력하세요."
              style={{
                ...inputStyle, height: '160px', padding: '18px',
                resize: 'none', lineHeight: '1.6',
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <button onClick={onClose} style={{
              width: '90px', height: '50px', borderRadius: '25px',
              background: '#E9E9EC', border: 'none', cursor: 'pointer',
              ...t('16px', 500, '#090909'),
            }}>취소</button>
            <button onClick={handleSave} style={{
              width: '120px', height: '50px', borderRadius: '25px',
              background: '#090909', border: 'none', cursor: 'pointer',
              ...t('16px', 500, '#FFFFFF'),
            }}>{isEdit ? '수정하기' : '추가하기'}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
